import { useState, useEffect } from "react"
import { flushSync } from "react-dom"
import { NavLink, useNavigate, useLocation } from "react-router-dom"
import { motion, AnimatePresence } from "framer-motion"
import {
  BarChart3,
  LayoutDashboard,
  UserPlus,
  Bell,
  Settings,
  Shield,
  ChevronLeft,
  ChevronRight,
  X,
  MessageCircle,
  Map as MapIcon,
  Globe,
  Building2,
  FileSpreadsheet,
  AlertCircle,
  Wallet,
  NotebookPen,
} from "lucide-react"
import { cn } from "@/lib/utils"
import { useAuth } from "@/hooks/useAuth"
import { useNotifications } from "@/hooks/useNotifications"
import { useUnreadMessagesCount } from "@/hooks/useUnreadMessagesCount"
import { useTenant } from "@/hooks/useTenant"
import { StageBadge } from "@/components/layout/StageBadge"
import { detectStage } from "@/lib/stage"
import { LifeBuoy } from "lucide-react"
import { podeEntrarNoGerenciamento } from "@/lib/allowlist"
import { useAcessoAoFinanceiro } from "@/contexts/AcessoAoFinanceiroContext"
import { AtalhoDoParceiro } from "@/components/layout/AtalhoDoParceiro"

interface NavItem {
  label: string
  to: string
  icon: React.ReactNode
  minRole?: "super_admin" | "network_manager" | "tenant_admin" | "backoffice" | "seller" | "ad_agency"
  /** Oculta o item em produção (creditos.invenia.com.br) — task #145.
   *  Página continua acessível via URL direta, só sai do menu. Útil pra
   *  features que ainda não estão prontas pra clientes finais mas estão
   *  visíveis em alfa/beta pra testes. */
  hideInProducao?: boolean
  badge?: number
  /** Lista exata de papéis, para página cuja regra NÃO é hierárquica.
   *  Hoje só a Landing Page: agência entra, vendedor não (ver a nota no
   *  `RoleGate` do router). Quando presente, substitui a checagem de minRole. */
  allow?: NonNullable<NavItem["minRole"]>[]
  /** #222/#223: visibilidade customizada (ex.: allowlist). Quando presente,
   *  substitui a checagem de minRole. O terceiro argumento traz o que o menu
   *  já sabe da sessão e não cabe em papel nem em id. */
  customVisible?: (
    userId: string | undefined,
    role: string | undefined,
    sessao: { podeAbrirOFinanceiro: boolean },
  ) => boolean
}

const baseNavItems: Omit<NavItem, "badge">[] = [
  {
    label: "Dashboard",
    to: "/dashboard",
    icon: <BarChart3 className="w-5 h-5" />,
  },
  {
    label: "Funil de Vendas",
    to: "/funil",
    icon: <LayoutDashboard className="w-5 h-5" />,
    minRole: "seller",
  },
  {
    label: "Mensagens",
    to: "/mensagens",
    icon: <MessageCircle className="w-5 h-5" />,
    minRole: "seller",
  },
  {
    label: "Notificações",
    to: "/notificacoes",
    icon: <Bell className="w-5 h-5" />,
    hideInProducao: true,
  },
  {
    label: "Novo Lead",
    to: "/novo-lead",
    icon: <UserPlus className="w-5 h-5" />,
    minRole: "seller",
  },
  {
    label: "Mapa de Clientes",
    to: "/mapa-clientes",
    icon: <MapIcon className="w-5 h-5" />,
    minRole: "ad_agency",
    hideInProducao: true,
  },
  {
    label: "Cobranças",
    to: "/cobrancas",
    icon: <AlertCircle className="w-5 h-5" />,
    minRole: "seller",
    hideInProducao: true,
  },
  {
    label: "Landing Page",
    to: "/landing-pages",
    icon: <Globe className="w-5 h-5" />,
    allow: ["ad_agency", "backoffice", "tenant_admin", "network_manager", "super_admin"],
  },
  {
    label: "Importar Leads (Meta)",
    to: "/importar-leads-meta",
    icon: <FileSpreadsheet className="w-5 h-5" />,
    minRole: "backoffice",
  },
  // A mensalidade que a unidade paga não tem item aqui: ela mora em
  // Configurações → Pagamento (D-B2), e o aviso de vencimento chega pela caixa
  // de notificações e pelo e-mail.
  {
    label: "Configurações",
    to: "/configuracoes/funil",
    icon: <Settings className="w-5 h-5" />,
    minRole: "tenant_admin",
  },
  {
    label: "Cadastrar nova ESC",
    to: "/cadastro-cliente",
    icon: <Building2 className="w-5 h-5" />,
    minRole: "super_admin",
  },
  {
    label: "Super Admin",
    to: "/super-admin",
    icon: <Shield className="w-5 h-5" />,
    minRole: "super_admin",
  },
  {
    label: "Gerenciamento",
    to: "/super-admin/gerenciamento",
    icon: <Building2 className="w-5 h-5" />,
    // #222/#223 e D-B1: visível para quem vê ao menos uma aba (super_admin +
    // allowlist nas Unidades; super_admin e network_manager nas Cobranças, só
    // com as telas da mensalidade no ar). A mesma régua do portão da rota.
    customVisible: podeEntrarNoGerenciamento,
  },
  {
    label: "Financeiro",
    to: "/financeiro",
    icon: <Wallet className="w-5 h-5" />,
    // #18: SEM `hideInProducao` de propósito: aquele filtro roda DEPOIS e
    // independe do `customVisible`, então esconderia o item até de quem usa o
    // Financeiro. A régua é a de `@/lib/financeiro/acesso` (super_admin sempre;
    // administrador e gerente de rede com unidade ligada), lida do mesmo valor
    // que o `PortaoDoFinanceiro` da rota usa. Enquanto a chave carrega, o item
    // não aparece.
    customVisible: (_userId, _role, sessao) => sessao.podeAbrirOFinanceiro,
  },
  // Vizinha da Central de Ajuda de propósito: as duas são ferramenta de
  // consulta, e quem procura "como se faz" olha para o mesmo canto do menu.
  // Sem `minRole`: quem mais consulta o mural é o vendedor.
  {
    label: "Anotações",
    to: "/anotacoes",
    icon: <NotebookPen className="w-5 h-5" />,
  },
  // Último do menu, por pedido do PM. Sem `minRole`: qualquer usuário logado
  // pede ajuda e lê as novidades.
  {
    label: "Suporte",
    to: "/suporte",
    icon: <LifeBuoy className="w-5 h-5" />,
  },
]

interface SidebarProps {
  mobileOpen?: boolean
  onMobileClose?: () => void
}

export function Sidebar({ mobileOpen = false, onMobileClose }: SidebarProps) {
  const [collapsed, setCollapsed] = useState(false)
  const { canAccess } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const { unreadCount } = useNotifications()
  // Task #149: contador global de mensagens não-lidas (badge no menu
  // "Mensagens"), mesmo padrão do badge de Notificações.
  //
  // Task #161 (2026-05-21): quando há unidade selecionada no menu
  // superior (activeTenantId), o badge soma apenas conversas daquela
  // unidade. Quando não há unidade selecionada (ver-todas), soma global.
  const { activeTenantId } = useTenant()
  const { unreadCount: unreadMessagesCount } = useUnreadMessagesCount(activeTenantId)

  // Garante que o overlay mobile fecha em qualquer navegação. Usamos
  // `location.key` (único por navegação) para que clicar na rota corrente
  // também dispare o fechamento — pathname não mudaria nesse caso.
  useEffect(() => {
    if (mobileOpen) onMobileClose?.()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key])

  // task #145: oculta itens marcados como hideInProducao quando estamos
  // em main (creditos.invenia.com.br). Em alfa/beta/local/preview, todos
  // os itens continuam visíveis pra facilitar QA/testes.
  const stage = detectStage()
  const isProducao = stage === "producao"

  const { user, role } = useAuth()
  const acessoAoFinanceiro = useAcessoAoFinanceiro()
  const sessaoDoMenu = { podeAbrirOFinanceiro: acessoAoFinanceiro.pode }
  const visibleItems: NavItem[] = baseNavItems
    .filter((item) => {
      // #222/#223: custom visibility takes precedence over minRole
      if (item.customVisible) return item.customVisible(user?.id, role, sessaoDoMenu)
      // Lista exata vem antes da hierarquia — é o caso das três páginas com
      // buraco no meio. O menu tem de dizer a MESMA coisa que a tranca da rota;
      // menu e rota discordando é como o buraco do /funil nasceu.
      if (item.allow) return role != null && (item.allow as string[]).includes(role)
      return !item.minRole || canAccess(item.minRole)
    })
    .filter((item) => !(isProducao && item.hideInProducao))
    .map((item) => ({
      ...item,
      badge: item.to === "/notificacoes"
        ? unreadCount
        : item.to === "/mensagens"
          ? unreadMessagesCount
          : undefined,
    }))

  // 🚨 O MENU NÃO ROLAVA. Ele mora dentro de caixas que cortam o que sobra
  // (`overflow-hidden` no AppLayout), e nenhuma caixa daqui podia ficar menor que
  // o próprio conteúdo: item de flex, por padrão, cresce até caber tudo, e a caixa
  // de fora corta calada. Numa janela de 600 px de altura sobram 544 para o menu,
  // e o de um super admin na produção pede 712: o fim da lista e o atalho do
  // parceiro ficavam abaixo da borda, sem barra e sem roda do mouse que os
  // alcançasse. A gaveta do celular tinha o mesmo defeito.
  //
  // O conserto são duas peças que só funcionam juntas:
  //   1. `min-h-0` no `<nav>` e na área que rola, para os dois poderem ficar
  //      menores que o conteúdo;
  //   2. `overflow-y-auto` na área que rola, para o que não coube virar rolagem.
  //
  // O botão de recolher fica FORA da área que rola, sempre à vista. O atalho do
  // parceiro fica DENTRO, depois do último item: ele é o último do menu, e
  // prendê-lo embaixo tiraria das seções do CreditOS justamente o espaço que falta
  // na tela baixa. `tests/components/SidebarRolagem.test.tsx` guarda as duas peças.
  //
  // 🚨 UM CONTEÚDO, DOIS DONOS DO "RECOLHIDO" (tarefa wdwxndz0h0). O menu do
  // computador e a gaveta do celular desenham este mesmo conteúdo, e os dois liam
  // o mesmo `collapsed`. Quem recolhia o menu em tela larga e depois estreitava a
  // janela abria a gaveta e via só os ícones, sem nome — e a seta que expande mora
  // num bloco que só aparece em tela larga, então no celular não havia volta.
  // Agora quem desenha diz se está recolhido: o computador passa `collapsed`, a
  // gaveta passa `false` sempre. O `collapsed` não é tocado pela gaveta, então ao
  // voltar para tela larga o menu continua do jeito que a pessoa deixou.
  // `tests/components/SidebarGavetaComNomes.test.tsx` guarda isso.
  const conteudoDoMenu = (recolhido: boolean, naGaveta: boolean) => (
    <nav className="flex min-h-0 flex-1 flex-col pt-4">
      {/* Seta de recolher: só existe no computador. Na gaveta ela nunca aparece
          (a gaveta some de `md` para cima, e a seta só aparece de `md` para cima),
          então nem é desenhada lá. */}
      {!naGaveta && (
        <div className="hidden md:flex shrink-0 justify-end px-3 mb-1">
          <button
            onClick={() => setCollapsed((v) => !v)}
            className="rounded-lg p-1.5 hover:bg-glass-hover transition text-[var(--text-secondary)]"
            aria-label={recolhido ? "Expandir menu" : "Recolher menu"}
          >
            {recolhido ? (
              <ChevronRight className="w-4 h-4" />
            ) : (
              <ChevronLeft className="w-4 h-4" />
            )}
          </button>
        </div>
      )}

      {/* A área que rola. `overflow-x-hidden` porque, com a rolagem vertical
          ligada, a horizontal liga sozinha, e o menu recolhido tem só 64 px.
          `pt-1` e `pb-4` dão folga para o anel de foco do primeiro e do último
          item não ser cortado pela borda. `overscroll-y-contain` faz o arrasto
          que chega ao fim do menu parar nele. */}
      <div
        data-testid="menu-rolavel"
        className="flex min-h-0 flex-1 flex-col overflow-y-auto overflow-x-hidden overscroll-y-contain pt-1 pb-4"
      >
        {/* Nav items */}
        <ul className="flex-1 space-y-1 px-2">
          {visibleItems.map((item) => (
            <li key={item.to}>
              <NavLink
                to={item.to}
                onClick={(e) => {
                  // No mobile, força o fechamento do menu ANTES de navegar.
                  // Sem flushSync, setMobileMenuOpen(false) é batched com o
                  // re-render da página destino — o aside só some ~500ms
                  // depois (especialmente para páginas pesadas como /funil
                  // que carregam kanban + queries). flushSync força o React
                  // a aplicar o setState imediatamente, re-renderizar (DOM do
                  // aside é removido), e só então o navigate ocorre.
                  if (mobileOpen) {
                    e.preventDefault()
                    flushSync(() => onMobileClose?.())
                    navigate(item.to)
                  }
                }}
                className={({ isActive }) =>
                  cn(
                    "flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors",
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent-blue)]",
                    // 🚨 O fundo do item ativo leva os canais e a transparência
                    // DENTRO do valor arbitrário. A forma que estava aqui pedia a
                    // opacidade por fora, com o modificador do Tailwind, e NÃO
                    // pintava nada: o Tailwind v3 descarta em silêncio a classe que
                    // junta cor arbitrária em `var()` com modificador de opacidade.
                    // O "você está aqui" do menu ficou sendo só a cor do texto, sem
                    // fundo nenhum, por tempo indeterminado.
                    // A explicação inteira, com os números, está em
                    // `src/styles/globals.css` (bloco CANAIS RGB); o portão
                    // `npm run classes-mortas` impede a volta, e
                    // `tests/components/SidebarItemAtivo.test.tsx` prova que este
                    // fundo específico continua saindo no CSS.
                    isActive
                      ? "bg-[rgb(var(--accent-blue-rgb)/0.15)] text-[var(--accent-blue)] dark:bg-[rgb(var(--accent-blue-rgb)/0.20)]"
                      : "text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-glass-hover"
                  )
                }
                title={recolhido ? item.label : undefined}
              >
                <span className="relative shrink-0">
                  {item.icon}
                  {item.badge != null && item.badge > 0 && (
                    <span
                      className="absolute -top-1.5 -right-1.5 min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center leading-none shadow-sm"
                    >
                      {item.badge > 99 ? "99+" : item.badge}
                    </span>
                  )}
                </span>
                <AnimatePresence initial={false}>
                  {!recolhido && (
                    <motion.span
                      initial={{ opacity: 0, width: 0 }}
                      animate={{ opacity: 1, width: "auto" }}
                      exit={{ opacity: 0, width: 0 }}
                      transition={{ duration: 0.15 }}
                      className="overflow-hidden whitespace-nowrap"
                    >
                      {item.label}
                    </motion.span>
                  )}
                </AnimatePresence>
              </NavLink>
            </li>
          ))}
        </ul>

        {/* Rodapé — fora da lista de navegação de propósito. Aqui mora o que não
            é seção do CreditOS. Hoje só o atalho do parceiro; se um dia entrar
            outro, entra ao lado deste e não no `<ul>` acima. Visível para todo
            mundo que tem login (sem `minRole`): é um link para uma página que
            explica um parceiro, não um poder — a mesma razão pela qual Suporte e
            Anotações também não filtram por papel. */}
        <AtalhoDoParceiro
          recolhido={recolhido}
          onNavegar={() => {
            if (mobileOpen) onMobileClose?.()
          }}
        />
      </div>
    </nav>
  )

  return (
    <>
      {/* Desktop sidebar */}
      <motion.aside
        className={cn(
          "hidden md:flex flex-col h-full",
          "backdrop-blur-[20px] bg-glass-card border-r border-glass-border"
        )}
        animate={{ width: collapsed ? 64 : 224 }}
        transition={{ duration: 0.2, ease: "easeInOut" }}
      >
        {conteudoDoMenu(collapsed, false)}
      </motion.aside>

      {/* Mobile overlay — conditional render simples (sem AnimatePresence)
          para garantir desmontagem imediata ao fechar. AnimatePresence +
          motion.aside vinha travando em mobile quando o estado mudava em
          paralelo a uma navegação, deixando o overlay zumbi sobre a página
          nova. CSS transitions cobrem a suavidade visual. */}
      {mobileOpen && (
        <>
          <div
            className={cn(
              "fixed inset-0 z-40 bg-black/30 backdrop-blur-sm md:hidden",
              "transition-opacity duration-200",
            )}
            onClick={onMobileClose}
          />
          <aside
            className={cn(
              "fixed left-0 top-0 bottom-0 z-50 w-64 md:hidden flex flex-col",
              "backdrop-blur-[30px] bg-glass-modal border-r border-glass-border",
              "animate-slide-in-left",
            )}
          >
            {/* Mobile close button */}
            <div className="flex items-center justify-between px-4 pt-4 pb-2">
              <button
                onClick={() => {
                  onMobileClose?.()
                  navigate("/dashboard")
                }}
                className="flex items-center gap-2 hover:opacity-80 transition"
                aria-label="Ir para Dashboard"
              >
                <img
                  src="/logo.png"
                  alt="CreditOS"
                  className="h-7 w-auto object-contain"
                  style={{ maxWidth: "none" }}
                />
                <StageBadge />
              </button>
              <button
                onClick={onMobileClose}
                className="rounded-lg p-1.5 hover:bg-glass-hover transition"
                aria-label="Fechar menu"
              >
                <X className="w-5 h-5 text-[var(--text-secondary)]" />
              </button>
            </div>
            {/* A gaveta mostra sempre os nomes, qualquer que seja o estado do
                menu do computador. */}
            {conteudoDoMenu(false, true)}
          </aside>
        </>
      )}
    </>
  )
}
